import React, { useState } from 'react'
import { Box, Button, Skeleton, Stack, useMediaQuery, useTheme } from '@mui/material'
import { makeStyles } from 'tss-react/mui'

import { J2Copy, J2Delete, J2Validate, K1Information } from '@e3dc-react/icons'
import { Paper } from '@e3dc-react/shell/Elements/Paper'
import { Typography } from '@e3dc-react/shell/Elements/Typography'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { format } from '@e3dc-react/shell/Libs/date'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useClient, useCreateClient, useDeleteClient } from 'src/Libs/user/hooks'
import { useTypedSelector } from 'src/State/RootReducer'
import useModals from 'src/Hooks/useModals'
import CopySecretDialog from './CopySecretDialog'
import ApiAccessInformationTile from './ApiAccessInformationTile'
import { useCopyToClipboard } from 'src/Elements/CopyToClipboard/hook'
import { API_ACCESS_CLIENT_ID, API_ACCESS_CREATE_BUTTON_ID, API_ACCESS_DELETE_BUTTON_ID, API_ACCESS_ID } from 'src/Libs/qa/ids'

const useStyles = makeStyles()(theme => ({
  clientRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    width: '100%',

    borderColor: theme.palette.shades.shade03,
    backgroundColor: theme.palette.shades.shade04,
    borderRadius: theme.spacing(2),
  },
  clientColumn: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    paddingInline: theme.spacing(4),
  },
  createdAtCard: {
    display: 'flex',
    width: 'auto',
    backgroundColor: theme.palette.common.white,
  },
}))

/**
 * Displays the user's API access client (Keycloak-backed) on their profile, letting them
 * create one if none exists, or view its creation date and delete it if one does.
 */
const ApiAccess: React.FC = () => {
  const userId = useTypedSelector(state => state.auth.userId)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { t } = useCustomTranslation()
  const { classes, cx } = useStyles()
  const { enqueueSuccessStack, enqueueErrorStack } = useSnackbar()
  const { openConfirmationModal } = useModals()
  const { copyToClipboard } = useCopyToClipboard()
  const { data: clientData, isLoading } = useClient(userId)
  const { mutateAsync: createClient, isPending: isCreatingClient } = useCreateClient(userId)
  const { mutateAsync: deleteClient, isPending: isDeletingClient } = useDeleteClient(userId)
  /** Stores secret that can be copy only one time */
  const [secretToCopy, setSecretToCopy] = useState<string | undefined>(undefined)
  const [isOpen, setIsOpen] = useState(false)

  const handleCreateClient = (): void => {
    createClient()
      .then(client => {
        enqueueSuccessStack(t('user.user-profile.api-access.client-successfully-created'))
        setSecretToCopy(client?.secret)
        setIsOpen(true)
      })
      .catch(() => enqueueErrorStack(t('user.user-profile.api-access.failed-to-create-client')))
  }

  const handleCLoseDialog = (): void => {
    setSecretToCopy(undefined)
    setIsOpen(false)
  }

  const handleDelete = (): void => {
    if (isDeletingClient) return

    deleteClient()
      .then(() => enqueueSuccessStack(t('user.user-profile.api-access.client-successfully-deleted')))
      .catch(() => enqueueErrorStack(t('user.user-profile.api-access.failed-to-delete-client')))
  }

  const handleConfirmDelete = (): void => {
    openConfirmationModal({
      title: t('user.user-profile.api-access.delete-client.title'),
      text: t('user.user-profile.api-access.delete-client.text'),
      onConfirm: handleDelete,
      buttonText: [t('common.no'), t('common.yes')],
    })
  }

  if (isLoading) {
    return (
      <Paper title={t('user.user-profile.api-access.title')} dataQa={API_ACCESS_ID}>
        <Skeleton sx={{ height: 120 }} />
      </Paper>
    )
  }

  const noClientView = (
    <Stack gap={2} mt={3}>
      <Typography variant="body2">{t('user.user-profile.api-access.no-client')}</Typography>
      <ApiAccessInformationTile
        message={t('user.user-profile.api-access.create-client-description')}
        leftIcon={<K1Information sx={{ color: theme.palette.grey.grey00 }} />}
      />
      <Box>
        <Button
          variant="outlined"
          fullWidth={isMobile}
          disabled={isCreatingClient}
          onClick={handleCreateClient}
          data-qa={API_ACCESS_CREATE_BUTTON_ID}
        >{`+ ${t('common.create')}`}</Button>
      </Box>
    </Stack>
  )

  const deleteIcon = (
    <J2Delete
      sx={{ cursor: isDeletingClient ? 'default' : 'pointer', opacity: isDeletingClient ? 0.5 : 1 }}
      onClick={handleConfirmDelete}
      data-qa={API_ACCESS_DELETE_BUTTON_ID}
    />
  )

  const createdAtTile = clientData?.createdAt && (
    <Box className={cx(classes.clientRow, classes.createdAtCard)}>
      <Typography variant="body2">{`${t('user.user-profile.api-access.created-at')} ${format(
        clientData.createdAt,
        t('common.format.date-fns-date')
      )}`}</Typography>
      <J2Validate sx={{ color: theme.palette.primary.main }} />
    </Box>
  )

  const clientId = (
    <Box display="flex" flexDirection="row" alignItems="center" gap={2} data-qa={API_ACCESS_CLIENT_ID}>
      <Typography variant="body1">{`${t('user.user-profile.api-access.client-id')}: ${clientData?.clientId}`}</Typography>
      <J2Copy sx={{ cursor: 'pointer' }} onClick={() => copyToClipboard(clientData?.clientId)} />
    </Box>
  )

  const clientView = isMobile ? (
    <Stack gap={2} mt={3} className={cx(classes.clientRow, classes.clientColumn)}>
      {clientId}
      <Stack flexDirection="row" justifyContent="space-between" alignItems="center" width="100%">
        {createdAtTile}
        {deleteIcon}
      </Stack>
    </Stack>
  ) : (
    <Stack gap={2} mt={3} className={classes.clientRow}>
      <Box display="flex" flex={1} justifyContent="space-between" alignItems="center">
        {clientId}
        {deleteIcon}
      </Box>
      {createdAtTile}
    </Stack>
  )

  return (
    <Paper title={t('user.user-profile.api-access.title')} dataQa={API_ACCESS_ID}>
      {clientData ? clientView : noClientView}
      {isOpen && secretToCopy && (
        <CopySecretDialog
          isOpen={isOpen}
          onClose={handleCLoseDialog}
          onConfirm={handleCLoseDialog}
          title={t('user.user-profile.api-access.copy-secret-title')}
          secret={secretToCopy}
        />
      )}
    </Paper>
  )
}

export default ApiAccess
